import { useEffect, useRef, useState } from 'react';

interface TimeInputProps {
  value?: string | null; // HH:mm
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  min?: string;
  max?: string;
  step?: number;
}

export function TimeInput({
  value,
  onChange,
  required = false,
  disabled = false,
  className = '',
}: TimeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatTime = (input: string) => {
    const digits = input.replace(/\D/g, '').slice(0, 4);

    if (digits.length <= 2) {
      return digits;
    }

    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const [displayValue, setDisplayValue] = useState(
    formatTime(value ?? '')
  );

  useEffect(() => {
    setDisplayValue(formatTime(value ?? ''));
  }, [value]);

  const getCursorPosition = (
    formatted: string,
    digitsBeforeCursor: number
  ) => {
    if (digitsBeforeCursor <= 0) {
      return 0;
    }

    let digitsSeen = 0;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitsSeen++;

        if (digitsSeen === digitsBeforeCursor) {
          return i + 1;
        }
      }
    }

    return formatted.length;
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawValue = event.target.value;

    const cursorPosition =
      event.target.selectionStart ?? rawValue.length;

    const digitsBeforeCursor = rawValue
      .slice(0, cursorPosition)
      .replace(/\D/g, '').length;

    const digits = rawValue
      .replace(/\D/g, '')
      .slice(0, 4);

    // Validation des heures
    if (digits.length >= 2) {
      const hours = Number(digits.slice(0, 2));

      if (hours > 23) {
        return;
      }
    }

    // Validation des minutes
    if (digits.length === 4) {
      const minutes = Number(digits.slice(2, 4));

      if (minutes > 59) {
        return;
      }
    }

    const formatted =
      digits.length <= 2
        ? digits
        : `${digits.slice(0, 2)}:${digits.slice(2)}`;

    setDisplayValue(formatted);
    onChange(formatted);

    requestAnimationFrame(() => {
      const input = inputRef.current;

      if (!input) return;

      const newPosition = getCursorPosition(
        formatted,
        digitsBeforeCursor
      );

      input.setSelectionRange(
        newPosition,
        newPosition
      );
    });
  };

  const handleBlur = () => {
    const digits = displayValue.replace(/\D/g, '');

    if (digits.length !== 4) {
      return;
    }

    const hours = Number(digits.slice(0, 2));
    const minutes = Number(digits.slice(2, 4));

    if (
      hours > 23 ||
      minutes > 59
    ) {
      return;
    }

    const normalized =
      `${String(hours).padStart(2, '0')}:` +
      `${String(minutes).padStart(2, '0')}`;

    setDisplayValue(normalized);
    onChange(normalized);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="HH:mm"
      maxLength={5}
      required={required}
      disabled={disabled}
      className={className}
      aria-label="Heure au format 24 heures"
    />
  );
}